import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getRecentColors, addRecentColor } from '../utils/recentColors';

export const useRecentColors = () => {
  const [recentColors, setRecentColors] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const pendingColorRef = useRef(null);

  // Load recent colors on mount and listen for updates
  useEffect(() => {
    const colors = getRecentColors();
    setRecentColors(colors);
    
    // Listen for external updates (from MultipleColorsField)
    const handleRecentColorsUpdate = (event) => {
      setRecentColors(event.detail);
    };
    
    window.addEventListener('recentColorsUpdated', handleRecentColorsUpdate);
    
    return () => {
      window.removeEventListener('recentColorsUpdated', handleRecentColorsUpdate);
    };
  }, []);

  // Set a pending color to be added when picker closes
  const setPendingColor = useCallback((color) => {
    if (color) {
      pendingColorRef.current = color;
    }
  }, []);

  const syncRecentColors = useCallback((updatedColors) => {
    if (!updatedColors) return;
    setRecentColors(updatedColors);
    // Dispatch event to sync other components (MultipleColorsField, etc.)
    window.dispatchEvent(new CustomEvent('recentColorsUpdated', { detail: updatedColors }));
  }, []);

  // Add the pending color to recent colors (called when picker closes)
  const addPendingColor = useCallback(() => {
    const pending = pendingColorRef.current;
    if (!pending) return;

    const updatedColors = addRecentColor(pending);
    syncRecentColors(updatedColors);
    pendingColorRef.current = null;
  }, [syncRecentColors]);

  // Add a color immediately (for re-selection from recent colors)
  const addColorImmediately = useCallback((color) => {
    if (color) {
      const updatedColors = addRecentColor(color);
      syncRecentColors(updatedColors);
    }
  }, [syncRecentColors]);

  // Calculate total pages:
  // - First page: 10 colors (there's only a "next" arrow)
  // - Middle pages: 9 colors (both prev/next arrows occupy 2 slots in an 11-col grid)
  // - Last page: remaining colors (only a "prev" arrow)
  const totalPages = useMemo(() => {
    const totalColors = recentColors.length;
    if (totalColors <= 10) {
      return 1;
    } else {
      const remainingAfterFirstPage = totalColors - 10;
      return 1 + Math.ceil(remainingAfterFirstPage / 9);
    }
  }, [recentColors.length]);

  const currentPageColors = useMemo(() => {
    const totalColors = recentColors.length;
    if (totalColors === 0) return [];
    if (totalColors <= 10) return recentColors;

    // Clamp in case recentColors changed and currentPage is now out of range
    const page = Math.min(Math.max(currentPage, 0), totalPages - 1);

    if (page === 0) {
      return recentColors.slice(0, 10);
    }

    const startIndex = 10 + (page - 1) * 9;

    if (page === totalPages - 1) {
      return recentColors.slice(startIndex, totalColors);
    }

    return recentColors.slice(startIndex, startIndex + 9);
  }, [recentColors, currentPage, totalPages]);

  // Keep currentPage within bounds when totalPages shrinks (e.g., external storage clear).
  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, Math.max(0, totalPages - 1)));
  }, [totalPages]);

  const goToNextPage = useCallback(() => {
    setCurrentPage((prev) => (prev < totalPages - 1 ? prev + 1 : prev));
  }, [totalPages]);

  const goToPrevPage = useCallback(() => {
    setCurrentPage((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  return {
    recentColors,
    setPendingColor,
    addPendingColor,
    addColorImmediately,
    currentPageColors,
    currentPage,
    totalPages,
    goToNextPage,
    goToPrevPage
  };
}; 